import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { api, formatUSD, formatApiErrorDetail } from "@/lib/api";
import { CurrencyDollar, ArrowLeft, Lock, Download } from "@phosphor-icons/react";
import { toast } from "sonner";
import { TransactionFeeBreakdown } from "@/lib/fees";

const STATUS_TONE = {
  pending: { label: "Awaiting payment", color: "#FF2D2D" },
  paid: { label: "Paid", color: "#22c55e" },
  cancelled: { label: "Cancelled", color: "#888" },
};

export default function CustomCharge() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [charge, setCharge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/charges/${id}`);
        setCharge(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Charge not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/checkout/session", {
        type: "custom_charge",
        reference_id: id,
        origin_url: window.location.origin,
      });
      if (data.free) {
        navigate(`/payment/success?session_id=${data.session_id}`);
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="text-[#a3a3a3]">Loading…</div>
      </ClientLayout>
    );
  }

  if (!charge) {
    return (
      <ClientLayout>
        <div className="border border-[#262626] bg-[#0a0a0a] p-8 text-center">
          <h1 className="font-display text-3xl">Charge not found</h1>
          <p className="text-[#a3a3a3] mt-2">It may have been cancelled or doesn't belong to your account.</p>
          <Link to="/dashboard" className="btn-outline mt-6 inline-flex px-5 py-3 text-sm uppercase tracking-wider">Back to dashboard</Link>
        </div>
      </ClientLayout>
    );
  }

  const tone = STATUS_TONE[charge.status] || STATUS_TONE.pending;

  return (
    <ClientLayout>
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#a3a3a3] hover:text-white mb-6" data-testid="charge-back">
        <ArrowLeft size={16} /> Dashboard
      </Link>

      <div className="max-w-xl mx-auto" data-testid="custom-charge-page">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Payment request</div>
        <h1 className="font-display text-5xl mt-2">Settle this charge.</h1>

        <div className="mt-8 border border-[#FF2D2D] bg-gradient-to-b from-[#FF2D2D]/10 to-transparent p-6">
          <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: tone.color }}>{tone.label}</div>
          <div className="font-display text-6xl mt-2 text-white tabular-nums" data-testid="charge-amount">{formatUSD(charge.amount)}</div>
          <div className="border-t border-[#262626] mt-5 pt-5">
            <div className="text-xs text-[#a3a3a3] uppercase tracking-wider mb-2">Description</div>
            <div className="text-sm whitespace-pre-wrap" data-testid="charge-description">{charge.description}</div>
          </div>
          <div className="text-xs text-[#666] mt-4">
            Issued {new Date(charge.created_at).toLocaleString()}
          </div>
        </div>

        {charge.status === "pending" && (
          <>
            <div className="mt-6">
              <TransactionFeeBreakdown base={Number(charge.amount)} testid="charge-fee-breakdown" />
            </div>
            <button
              onClick={pay}
              disabled={paying}
              data-testid="pay-charge-btn"
              className="btn-primary w-full mt-4 px-6 py-4 text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <CurrencyDollar size={16} weight="bold" />
              {paying ? "Redirecting…" : `Pay now`}
            </button>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/charges/${id}/invoice.pdf`}
              target="_blank"
              rel="noreferrer"
              data-testid="download-charge-quote"
              className="mt-3 text-xs uppercase tracking-wider text-[#a3a3a3] hover:text-[#FF2D2D] flex items-center justify-center gap-1.5"
            >
              <Download size={11} /> Download invoice PDF
            </a>
          </>
        )}
        {charge.status === "paid" && (
          <div className="mt-6 border border-[#22c55e]/40 bg-[#22c55e]/5 p-4 text-sm text-[#22c55e] flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Lock size={14} weight="fill" /> This charge has been paid. Thank you.
            </div>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/charges/${id}/invoice.pdf`}
              target="_blank"
              rel="noreferrer"
              data-testid="download-charge-receipt"
              className="text-xs uppercase tracking-wider px-3 py-1.5 border border-[#22c55e]/40 text-[#22c55e] hover:bg-[#22c55e]/10 flex items-center gap-1.5"
            >
              <Download size={11} /> Receipt PDF
            </a>
          </div>
        )}
        {charge.status === "cancelled" && (
          <div className="mt-6 border border-[#262626] bg-[#0a0a0a] p-4 text-sm text-[#a3a3a3]">
            This charge was cancelled and is no longer payable.
          </div>
        )}
      </div>
    </ClientLayout>
  );
}
